import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';
import {States} from "../../shared/config/constant.module";
import template from './batchUsageReport.tpl.html';
import {Plant} from "../../models/navigation";
import {apsCommon} from "../../shared/dto/aps.common";
import {apsViewModels} from "../../shared/dto/APS.ViewModels";
import {StateParams} from "../../common";
import LoaderServiceName, {LoaderService} from "../../components/loader/loader.service";
import DateUtilityService from "../../services/utility/dateutility.service";
import BatchFormulasServiceName, {BatchFormulasService} from "../../services/batches/batchFormulas.service";
import NavigationServiceName, {NavigationService} from "../../services/navigation/navigation.service";

let requires = [LoaderServiceName, BatchFormulasServiceName, NavigationServiceName];

interface BatchUsageReportController {
  plants: Plant[];
  selectedPlant: Plant;
  tanks: apsCommon.IdAndName[];
  selectedTank: apsCommon.IdAndName;
  startDate: Date;
  endDate: Date;
  form: angular.IFormController;

  results: apsViewModels.BatchFormulaUsageDetail[];
  searchPerformed: boolean;

  generateReport(): void;
  formatTime(time: Date): string;
  formatNumber(number: number): string;
  updatePlant(): void;
}

export interface BatchUsageReportParams extends StateParams {
  reportPlantId: string;
  reportTankId: string;
  startDate: string;
  endDate: string;
  autoRun: boolean;
}

class BatchUsageReportCtrl implements BatchUsageReportController {
  plants: Plant[];
  selectedPlant: Plant;
  tanks: apsCommon.IdAndName[];
  selectedTank: apsCommon.IdAndName;
  startDate: Date;
  endDate: Date;
  form: angular.IFormController;
  private $stateParams: BatchUsageReportParams;
  private $state: angular.ui.IStateService;

  results: apsViewModels.BatchFormulaUsageDetail[];
  searchPerformed: boolean;

  loaderService: LoaderService;
  private $q: angular.IQService;
  private batchFormulasService: BatchFormulasService;

  constructor(
    $stateParams: BatchUsageReportParams,
    $state: angular.ui.IStateService,
    $q: angular.IQService,
    navigationService: NavigationService,
    batchFormulasService: BatchFormulasService,
    loaderService: LoaderService
  ) {
    this.$q = $q;
    this.$state = $state;
    this.$stateParams = $stateParams;
    this.loaderService = loaderService;
    this.batchFormulasService = batchFormulasService;

    var today = moment().startOf('day');
    this.startDate = today.clone().subtract(1, 'month').toDate();
    this.endDate = today.toDate();

    var promise = navigationService.getNavigation().then((navModel) => {
      this.plants = navModel.plants();
      this.selectedPlant = navModel.selectedPlant;
      this.updatePlant();

      return this.loadState($stateParams);
    });

    loaderService.loadingPromise(promise);
  }

  loadState(params: BatchUsageReportParams): angular.IPromise<any> {
    if (params.reportPlantId && params.reportPlantId !== params.plantId) {
      var plant = _.find(this.plants, {id: params.reportPlantId});
      if (plant) {
        this.selectedPlant = plant;
        this.updatePlant();
      }
    }

    if (params.reportTankId) {
      var tank = _.find(this.tanks, {id: params.reportTankId});
      if (tank) {
        this.selectedTank = tank;
      }
    }

    if (params.startDate) {
      var startDate = moment(params.startDate);
      if (startDate.isValid()) {
        this.startDate = startDate.toDate();
      }
    }

    if (params.endDate) {
      var endDate = moment(params.endDate);
      if (endDate.isValid()) {
        this.endDate = endDate.toDate();
      }
    }

    if (params.autoRun) {
      return this.runUsageReport();
    }

    return this.$q.when();
  }

  saveState() {
    var reportPlantId = this.selectedPlant ? this.selectedPlant.id : undefined;
    if (reportPlantId === this.$stateParams.plantId) {
      reportPlantId = undefined;
    }
    var reportTankId = this.selectedTank ? this.selectedTank.id : undefined;
    
    var params: BatchUsageReportParams = {
      plantId: this.$stateParams.plantId,
      shopId: this.$stateParams.shopId,
      userId: this.$stateParams.userId,
      reportPlantId: reportPlantId,
      reportTankId: reportTankId,
      startDate: this.startDate ? this.startDate.toISOString() : undefined,
      endDate: this.endDate ? this.endDate.toISOString() : undefined,
      autoRun: true
    };

    this.$state.transitionTo(States.BATCH_FORMULA_USAGE_REPORT, params);
  }

  generateReport() {
    if (this.form.$invalid) {
      return;
    }

    this.saveState();

    this.loaderService.loadingPromise(this.runUsageReport());
  }

  private runUsageReport(): angular.IPromise<any> {
    var startDateStart = moment(this.startDate).startOf('day').toDate();
    var tzOffsetStartDate = DateUtilityService.localToTimeZone(startDateStart, this.selectedPlant.timeZoneId).toDate();

    var endDateEnd = moment(this.endDate || new Date()).endOf('day').toDate();
    var tzOffsetEndDate = DateUtilityService.localToTimeZone(endDateEnd, this.selectedPlant.timeZoneId).toDate();

    return this.batchFormulasService
      .getUsageReport(this.selectedPlant.id, this.selectedTank.id, tzOffsetStartDate, tzOffsetEndDate)
      .then(reportItems => {
        this.results = reportItems;
        this.searchPerformed = true;
      });
  }

  formatTime(time: Date): string {
    return DateUtilityService.formatDate(time, this.selectedPlant.timeZoneId);
  }

  formatNumber(number: number): string {
    return number.toFixed(4);
  }

  updatePlant(): void {
    this.tanks = this.selectedPlant.tanks;
    this.selectedTank = this.tanks[0];
  }
}

export default angular.module('aps.controller.batchUsageReport', requires)
  .config(function ($stateProvider: angular.ui.IStateProvider) {
    $stateProvider.state({
      name: States.BATCH_FORMULA_USAGE_REPORT,
      url: '/admin/reports/batchUsage?startDate&endDate&autoRun&reportPlantId&reportTankId',
      params: {
        startDate: {dynamic: true},
        endDate: {dynamic: true},
        autoRun: {dynamic: true},
        reportPlantId: {dynamic: true},
        reportTankId: {dynamic: true}
      },
      controller: 'BatchUsageReportCtrl as vm',
      template: template,
      data: {
        pageTitle: 'Batch Formula Usage Report'
      }
    });
  })
  .controller('BatchUsageReportCtrl', BatchUsageReportCtrl)
  .name;


// WEBPACK FOOTER //
// ./src/app/controllers/batches/batchUsageReport.controller.ts